import { ChevronLeft, ChevronRight } from 'lucide-react';

interface BlogPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

// Compact page list with at most 7 cells: first, last, current ± 1, and ellipsis.
function buildPageList(current: number, total: number): Array<number | 'ellipsis-l' | 'ellipsis-r'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | 'ellipsis-l' | 'ellipsis-r'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('ellipsis-l');
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push('ellipsis-r');
  out.push(total);
  return out;
}

export function BlogPagination({ currentPage, totalPages, onPageChange }: BlogPaginationProps) {
  if (totalPages <= 1) return null;
  const pages = buildPageList(currentPage, totalPages);

  const btnBase =
    'inline-flex items-center justify-center min-w-[2.5rem] h-10 px-3 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accessible-red-600 focus:ring-offset-2 dark:focus:ring-offset-accessible-gray-900';
  const btnInactive =
    'bg-white text-accessible-gray-700 hover:bg-accessible-gray-100 dark:bg-accessible-gray-800 dark:text-white/90 dark:hover:bg-accessible-gray-700 high-contrast:bg-black high-contrast:text-white high-contrast:border high-contrast:border-white';
  const btnActive =
    'bg-accessible-red-600 text-white hover:bg-accessible-red-700';
  const btnDisabled =
    'opacity-40 cursor-not-allowed pointer-events-none';

  return (
    <nav aria-label="Paginacja wpisów" className="mt-12 flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`${btnBase} ${btnInactive} ${currentPage === 1 ? btnDisabled : ''}`}
        aria-label="Poprzednia strona"
      >
        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
        <span className="sr-only sm:not-sr-only sm:ml-1">Poprzednia</span>
      </button>

      <ul className="flex items-center gap-1">
        {pages.map((p) =>
          p === 'ellipsis-l' || p === 'ellipsis-r' ? (
            <li key={p} aria-hidden="true" className="px-2 text-accessible-gray-500 dark:text-white/60">
              …
            </li>
          ) : (
            <li key={p}>
              <button
                type="button"
                onClick={() => onPageChange(p)}
                aria-current={p === currentPage ? 'page' : undefined}
                aria-label={p === currentPage ? `Strona ${p}, aktualna` : `Przejdź na stronę ${p}`}
                className={`${btnBase} ${p === currentPage ? btnActive : btnInactive}`}
              >
                {p}
              </button>
            </li>
          )
        )}
      </ul>

      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`${btnBase} ${btnInactive} ${currentPage === totalPages ? btnDisabled : ''}`}
        aria-label="Następna strona"
      >
        <span className="sr-only sm:not-sr-only sm:mr-1">Następna</span>
        <ChevronRight className="w-4 h-4" aria-hidden="true" />
      </button>
    </nav>
  );
}
